import { useCallback, useEffect, useState } from "react";
import { useDialog } from "../../Common/Dialog";
import { useLoading } from "./form";
import actions, {
  DisplayFullOrganization,
  OrganizationAddressUpdateRequest,
} from "../../actions";
import { KYDPixelSettingsUpdate } from "@common/interfaces/KYDEvent";

export const useOrganizationSettings = () => {
  const [organization, setOrganization] =
    useState<DisplayFullOrganization | null>(null);
  const [loading, setLoading] = useLoading<{
    organization: boolean;
  }>({
    organization: false,
  });
  const dialog = useDialog();

  const fetchSettings = useCallback(
    async (silent?: boolean): Promise<DisplayFullOrganization | null> => {
      if (!silent) {
        setLoading("organization", true);
      }
      let org;
      try {
        const _ = await actions.ADMIN.fetchOrganization();
        if (_) {
          setOrganization(_);
          org = _;
        }
      } catch (err) {
        dialog({ text: err.message });
      }
      setLoading("organization", false);
      return org;
    },
    [setLoading, dialog]
  );

  const updateSettings = useCallback(
    async (settings: any) => {
      try {
        await actions.ADMIN.updateOrganization("settings", settings);
        await fetchSettings(true);
        return true;
      } catch (err) {
        dialog({ text: err.message });
        return false;
      }
    },
    [fetchSettings, dialog]
  );

  const updateRebate = useCallback(
    async (rebate: any) => {
      try {
        await actions.ADMIN.updateOrganization("rebate", rebate);
        await fetchSettings(true);
        return true;
      } catch (err) {
        dialog({ text: err.message });
        return false;
      }
    },
    [fetchSettings, dialog]
  );

  const updateAddress = useCallback(
    async (address: OrganizationAddressUpdateRequest) => {
      try {
        await actions.ADMIN.updateOrganization("address", address);
        await fetchSettings(true);
        return true;
      } catch (err) {
        dialog({ text: err.message });
        return false;
      }
    },
    [fetchSettings, dialog]
  );

  const updatePixels = useCallback(
    async (pixels: KYDPixelSettingsUpdate[]) => {
      try {
        await actions.ADMIN.updateOrganizationPixels({ pixels });
        await fetchSettings(true);
        return true;
      } catch (err) {
        dialog({ text: err.message });
        return false;
      }
    },
    [fetchSettings, dialog]
  );

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return {
    organization,
    loading: {
      organization: loading.organization as boolean,
    },
    refetch: (options?: any) => fetchSettings(options?.silent),
    updateSettings,
    updateRebate,
    updateAddress,
    updatePixels,
  };
};
